/**
 * The lecture-aware card at the top of Home (slice B, P1-1): "Nästa: F03 · tors 12 nov", the
 * lecture's title, its Y&F sections and first two objectives, and two hand-offs to the lecture
 * setup page: **Förbered F03** and **Repetera F02** (`/practice?course=HT2026&lecture=…`, which
 * still waits for "Starta"). Before F01 it says when the course starts; after F12 it offers the
 * last lecture and a practice exam. A lecture that is unpublished or has an empty pool gets no
 * button. The state comes from the catalogue dates and the Europe/Stockholm day
 * (`shared/courseCalendar.ts`); nothing is stored.
 *
 * It renders nothing while the catalogue loads or cannot be read, or while an exam is running:
 * Home works without it.
 */
import { Link } from "react-router";
import type { PublicLecture } from "@content/courseSchema";
import { useTopicsAndChapters } from "@client/api/topics";
import { useLocalizedText } from "@client/components/Localized";
import { Card } from "@client/components/ui";
import { useExamMode } from "@client/app/examMode";
import { useLang, useT } from "@client/i18n";
import { formatDayShort } from "@client/lib/format";
import { useIsTeacher } from "@client/app/auth";
import {
  canPractise,
  isUnreleased,
  lecturePracticeHref,
  lectureSections,
  releaseText,
  useLectureTimeline,
} from "./course";

const PRIMARY =
  "inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-accent-700 px-4 text-sm font-medium text-white transition-colors hover:bg-accent-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:bg-accent-600 dark:hover:bg-accent-500";
const SECONDARY =
  "inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-ink-300 bg-white px-4 text-sm font-medium text-ink-800 transition-colors hover:bg-ink-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100 dark:hover:bg-ink-800";

/** The objectives shown on the card; the setup page lists them all. */
const OBJECTIVES_SHOWN = 2;

export function LectureCard() {
  const t = useT();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const { examActive } = useExamMode();
  const { timeline } = useLectureTimeline();
  const topics = useTopicsAndChapters();
  const teacher = useIsTeacher();

  if (examActive || !timeline) return null;
  const { focus, previous, today, done } = timeline;
  const bookOf = (id: string) => topics.data?.topics.find((topic) => topic.id === id)?.book;
  const day = (lecture: PublicLecture) => formatDayShort(lecture.date, lang);

  let title: string;
  if (!focus) title = t("content.lecture.card.allDone");
  else if (today) title = t("content.lecture.card.today", { id: focus.id });
  else if (done.length === 0)
    title = t("content.lecture.card.starts", { date: day(focus), id: focus.id });
  else title = t("content.lecture.card.next", { id: focus.id, date: day(focus) });

  const shown = focus ?? previous;
  const sections = shown ? lectureSections(shown.topics, bookOf) : [];

  return (
    <Card
      title={title}
      description={shown ? `${shown.id} · ${localize(shown.title)}` : undefined}
      className="mb-6"
      aria-label={t("content.lecture.card.label")}
    >
      {shown && sections.length > 0 && (
        <p className="mb-2 text-sm text-ink-600 dark:text-ink-400">
          {t("content.lecture.reading", { sections: sections.join(", ") })}
        </p>
      )}
      {shown && shown.objectives.length > 0 && (
        <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-ink-700 dark:text-ink-200">
          {shown.objectives.slice(0, OBJECTIVES_SHOWN).map((objective, i) => (
            <li key={i}>{localize(objective)}</li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {focus && canPractise(focus, teacher) && (
          <Link to={lecturePracticeHref(focus.id)} className={PRIMARY}>
            {t("content.lecture.prepare", { id: focus.id })}
          </Link>
        )}
        {focus && isUnreleased(focus, teacher) && (
          <p className="text-sm font-medium text-ink-700 dark:text-ink-200">
            {focus.id}: {releaseText(focus.release, t, lang)}
          </p>
        )}
        {previous && canPractise(previous, teacher) && (
          <Link to={lecturePracticeHref(previous.id)} className={focus ? SECONDARY : PRIMARY}>
            {t("content.lecture.review", { id: previous.id })}
          </Link>
        )}
        {previous && isUnreleased(previous, teacher) && (
          <p className="text-sm font-medium text-ink-700 dark:text-ink-200">
            {previous.id}: {releaseText(previous.release, t, lang)}
          </p>
        )}
        {!focus && (
          <Link to="/exam" className={SECONDARY}>
            {t("content.lecture.card.exam")}
          </Link>
        )}
        <Link
          to="/library/topics?view=lectures"
          className="text-sm text-accent-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 dark:text-accent-400"
        >
          {t("content.lecture.card.all")}
        </Link>
      </div>
    </Card>
  );
}
